// This file will contain the handlers for individual segments,
// such as onInput, onKeyDown, etc.

import React from 'react'

import { Segment, User } from '../types'
import { getCaretPosition } from '../utils/domUtils'
import { normalizeSegments } from '../utils/segmentUtils'

import { UseEditorHandlersProps } from './useEditorHandlers'
import { UseMentionLogicReturn } from './useMentionLogic'

export const handleKeyDownSegment = (
  e: React.KeyboardEvent<HTMLSpanElement>,
  index: number,
  props: UseEditorHandlersProps,
  mentionLogic: UseMentionLogicReturn,
  commitMention: (user: User) => void,
  isAnnotating?: boolean,
) => {
  const {
    segments,
    setActiveIndex,
    nextCursorPos,
    nextId,
    setSegments,
    setIsNavigating,
    setGlobalCursorPos,
    filteredUsers,
    activeSuggestionIndex,
    setActiveSuggestionIndex,
    isMentionPopupOpen,
    editorRef,
    setIsMentionPopupOpen,
    setMentionQuery,
    selectionRequestRef,
    users,
  } = props

  // Handle deletion of a wide selection first
  const selection = window.getSelection()
  if (
    selection &&
    !selection.isCollapsed &&
    (e.key === 'Backspace' || e.key === 'Delete')
  ) {
    if (
      editorRef.current &&
      selection.anchorNode &&
      editorRef.current.contains(selection.anchorNode)
    ) {
      e.preventDefault()
      const newId = `seg-${nextId.current++}`
      setSegments([{ id: newId, type: 'text', content: '' }])
      setActiveIndex(0)
      nextCursorPos.current = 0
      setIsMentionPopupOpen(false)
      setMentionQuery('')
      return
    }
  }

  if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault()
    // Select all on first press
    selectionRequestRef.current = { type: 'all' }
    setIsNavigating(true)
    return
  }

  // --- Trigger: @ for mentions ---
  if (e.key === '@') {
    const el = e.currentTarget
    const content = el.textContent || ''
    const caretPos = getCaretPosition(el)

    // Only trigger if it's the start of the segment or preceded by a space.
    if (caretPos === 0 || content[caretPos - 1] === ' ') {
      // We've decided to create a mention. Prevent default browser action.
      e.preventDefault()
      // The mention logic will split the segment, so we just pass the index.
      mentionLogic.startMention(index)
      return // Stop further processing
    }
    // If the condition is not met, do nothing and let the browser type '@'.
  }

  // --- NEW: Handle typing in a finalized 'mention' to start editing it ---
  if (
    segments[index]?.type === 'mention' &&
    e.key.length === 1 &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.altKey
  ) {
    e.preventDefault()

    const selection = window.getSelection()
    if (!selection) return

    const range = selection.getRangeAt(0)
    const { startOffset, endOffset } = range
    
    const currentContent = segments[index].content || ''
    const newContent =
      currentContent.substring(0, startOffset) + e.key + currentContent.substring(endOffset)

    const newSegment: Segment = {
      ...segments[index],
      type: 'mention_in_progress',
      content: newContent,
    }

    const newSegments = [...segments]
    newSegments[index] = newSegment
    setSegments(newSegments)

    nextCursorPos.current = startOffset + e.key.length
    mentionLogic.updateMention(newContent)
    return
  }

  // --- Handling keys when inside a mention_in_progress segment ---
  const currentSegment = segments[index]
  if (currentSegment?.type === 'mention_in_progress') {
    // Handle Backspace at the start of an in-progress mention
    if (e.key === 'Backspace') {
      e.preventDefault()

      const el = e.currentTarget
      const selection = window.getSelection()
      if (!selection?.rangeCount) return

      const range = selection.getRangeAt(0)
      const { startOffset, endOffset } = range
      const currentContent = el.textContent || ''

      if (startOffset <= 1 && endOffset <= 1) {
        mentionLogic.cancelMention()
        return
      }

      let newContent
      let newCursorPos

      if (startOffset === endOffset) {
        // Standard backspace, no selection
        newContent =
          currentContent.slice(0, startOffset - 1) +
          currentContent.slice(startOffset)
        newCursorPos = startOffset - 1
      } else {
        // Deleting a selected range
        newContent =
          currentContent.slice(0, startOffset) + currentContent.slice(endOffset)
        newCursorPos = startOffset
      }

      if (!newContent.startsWith('@')) {
        mentionLogic.cancelMention()
        return
      }

      const newSegments = [...segments]
      newSegments[index] = { ...newSegments[index], content: newContent }
      setSegments(newSegments)

      mentionLogic.updateMention(newContent)

      nextCursorPos.current = newCursorPos
      return
    }

    if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab'].includes(e.key)) {
      e.preventDefault()

      if (e.key === 'ArrowUp') {
        setActiveSuggestionIndex((prev: number) =>
          prev > 0 ? prev - 1 : filteredUsers.length - 1,
        )
      } else if (e.key === 'ArrowDown') {
        setActiveSuggestionIndex((prev: number) =>
          prev < filteredUsers.length - 1 ? prev + 1 : 0,
        )
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (filteredUsers[activeSuggestionIndex]) {
          commitMention(
            filteredUsers[activeSuggestionIndex],
          )
        } else {
          mentionLogic.cancelMention()
        }
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      mentionLogic.cancelMention()
      return
    }

    if (e.key === ' ') {
      e.preventDefault()

      const currentContent = e.currentTarget.textContent || ''
      const query = currentContent.substring(1)

      // Find the best (longest) matching user at the start of the query
      let bestMatch: User | null = null
      for (const user of users) {
        if (query.toLowerCase().startsWith(user.name.toLowerCase())) {
          if (!bestMatch || user.name.length > bestMatch.name.length) {
            bestMatch = user
          }
        }
      }

      if (bestMatch) {
        const restOfQuery = query.substring(bestMatch.name.length)

        // Only commit the mention if the text after the name is empty or starts with a space
        if (restOfQuery.length === 0 || restOfQuery.startsWith(' ')) {
          const mentionSegment: Segment = {
            ...currentSegment,
            type: 'mention',
            content: `@${bestMatch.name}`,
          }

          const textSegment: Segment = {
            id: `seg-${nextId.current++}`,
            type: 'text',
            // Prepend the original space, but trim any extra space from the beginning of the rest
            content: ' ' + restOfQuery.trimStart(),
          }

          const newSegments = [...segments]
          newSegments.splice(index, 1, mentionSegment, textSegment)
          const normalized = normalizeSegments(newSegments)

          setSegments(normalized)
          const newIndex = normalized.findIndex(s => s.id === textSegment.id)
          setActiveIndex(newIndex)
          nextCursorPos.current = 0
          setIsMentionPopupOpen(false)
          return
        }
      }

      // Fallback: If no valid match is found, cancel the mention and convert to text
      mentionLogic.cancelMention(true)
      return
    }
  }

  const el = e.currentTarget
  const caretPos = getCaretPosition(el)

  // --- Handle Backspace and Delete at segment boundaries ---
  if (e.key === 'Backspace' && caretPos === 0 && index > 0) {
    e.preventDefault()
    const prevSegment = segments[index - 1]

    if (prevSegment?.type === 'mention') {
      // Convert the mention to 'in-progress' to enable editing
      const newSegments = [...segments]
      newSegments[index - 1] = {
        ...prevSegment,
        type: 'mention_in_progress',
      }
      setSegments(newSegments)

      // Set focus and cursor position
      setActiveIndex(index - 1)
      nextCursorPos.current = prevSegment.content.length
      mentionLogic.updateMention(prevSegment.content)
    } else if (prevSegment?.type === 'mention_in_progress') {
      // Delete the entire in-progress mention segment
      const newSegments = [...segments]
      newSegments.splice(index - 1, 1)

      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)
      setActiveIndex(index - 1)
      nextCursorPos.current = segments[index - 1].content.length
    } else if (prevSegment) {
      // Default behavior: merge with previous text segment
      const newSegments = [...segments]
      const currentContent = newSegments[index].content
      const originalPrevLength = prevSegment.content.length

      newSegments[index - 1].content += currentContent
      newSegments.splice(index, 1)

      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)

      nextCursorPos.current = originalPrevLength
      setActiveIndex(index - 1)
    }
  }

  if (
    e.key === 'Delete' &&
    caretPos === el.textContent?.length &&
    index < segments.length - 1
  ) {
    e.preventDefault()
    const nextSegment = segments[index + 1]

    if (nextSegment?.type === 'mention') {
      // Convert the mention to 'in-progress' to enable editing
      const newSegments = [...segments]
      newSegments[index + 1] = {
        ...nextSegment,
        type: 'mention_in_progress',
      }
      setSegments(newSegments)

      // Set focus and cursor position
      setActiveIndex(index + 1)
      nextCursorPos.current = 1 // After the '@'
      mentionLogic.updateMention(nextSegment.content)
    } else if (nextSegment?.type === 'mention_in_progress') {
      // Delete the entire in-progress mention segment
      const newSegments = [...segments]
      newSegments.splice(index + 1, 1)

      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)
      setActiveIndex(index)
      nextCursorPos.current = segments[index].content.length
    } else if (nextSegment) {
      // Default behavior: merge with next text segment
      const newSegments = [...segments]
      const nextContent = newSegments[index + 1].content
      const originalCurrentLength = newSegments[index].content.length
      
      newSegments[index].content += nextContent
      newSegments.splice(index + 1, 1)

      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)

      nextCursorPos.current = originalCurrentLength
      setActiveIndex(index)
    }
  }

  // --- Handle Arrow keys to enter navigation mode ONLY at boundaries ---
  if (e.key === 'ArrowLeft' && caretPos === 0 && index > 0) {
    // We are at the beginning, prevent default and switch to navigation
    e.preventDefault()
    setIsNavigating(true)
    // Calculate global position and move cursor there
    const globalCursorPos = segments
      .slice(0, index)
      .reduce((acc, seg) => acc + seg.content.length, 0)
    setGlobalCursorPos(globalCursorPos - 1)
    return
  }

  const textLength = el.textContent?.length || 0
  if (e.key === 'ArrowRight' && caretPos === textLength && index < segments.length - 1) {
    // We are at the end, prevent default and switch to navigation
    e.preventDefault()
    setIsNavigating(true)
    const globalCursorPos = segments
      .slice(0, index + 1)
      .reduce((acc, seg) => acc + seg.content.length, 0)
    // Move the cursor one step INTO the next segment
    setGlobalCursorPos(globalCursorPos + 1)
    return
  }

  // For ArrowUp/Down or other arrows inside the text,
  // we let the browser handle it natively.

  // --- Spacebar is now handled by the default onInput event ---
  // The complex logic for navigation mode has been removed for performance.

  // --- Handle Shift+Enter for new lines ---
  if (e.key === 'Enter' && e.shiftKey) {
    e.preventDefault()

    const currentSegment = segments[index]
    if (currentSegment.type === 'text') {
      const caretPos = getCaretPosition(e.currentTarget)
      const textBefore = currentSegment.content.slice(0, caretPos)
      const textAfter = currentSegment.content.slice(caretPos)

      const beforeSegment: Segment = { ...currentSegment, content: textBefore }
      const newlineSegment: Segment = {
        id: `seg-${nextId.current++}`,
        type: 'text',
        content: '\n',
      }
      const afterSegment: Segment = {
        id: `seg-${nextId.current++}`,
        type: 'text',
        content: textAfter,
      }

      const newSegments = [...segments]
      newSegments.splice(index, 1, beforeSegment, newlineSegment, afterSegment)

      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)

      const finalNewIndex = normalized.findIndex(s => s.id === afterSegment.id)
      setActiveIndex(finalNewIndex !== -1 ? finalNewIndex : 0)
      nextCursorPos.current = 0
    }
    return
  }

  // --- Centralized handler for all printable characters ---
  if (
    e.key.length === 1 &&
    !e.metaKey &&
    !e.ctrlKey &&
    (currentSegment.type === 'text' || currentSegment.type === 'annotate')
  ) {
    e.preventDefault()

    const desiredType = isAnnotating ? 'annotate' : 'text'

    const selection = window.getSelection()
    if (!selection) return

    const range = selection.getRangeAt(0)
    const start = range.startOffset
    const end = range.endOffset

    const currentContent = el.textContent || ''

    if (currentSegment.type === desiredType) {
      const newContent =
        currentContent.substring(0, start) + e.key + currentContent.substring(end)
      const newSegments = [...segments]
      newSegments[index] = { ...newSegments[index], content: newContent }
      setSegments(newSegments)
      nextCursorPos.current = start + e.key.length
    } else {
      const contentBefore = currentContent.substring(0, start)
      const contentAfter = currentContent.substring(end)

      const beforeSegment: Segment = {
        ...currentSegment,
        content: contentBefore,
      }
      const newTypedSegment: Segment = {
        id: `seg-${nextId.current++}`,
        type: desiredType,
        content: e.key,
      }
      if (desiredType === 'annotate') {
        newTypedSegment.data = 'annotate '
      }
      const afterSegment: Segment = {
        id: `seg-${nextId.current++}`,
        type: currentSegment.type,
        content: contentAfter,
      }
      const newSegments = [...segments]
      newSegments.splice(index, 1, beforeSegment, newTypedSegment, afterSegment)
      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)
      const newIndex = normalized.findIndex(s => s.id === newTypedSegment.id)
      if (newIndex !== -1) {
        setActiveIndex(newIndex)
        nextCursorPos.current = 1
      }
    }
    return
  }

  if (isMentionPopupOpen && ['Enter', 'Tab'].includes(e.key)) {
    e.preventDefault()
    const user = filteredUsers[activeSuggestionIndex]
    if (user) {
      commitMention(user)
    }
    return
  }

  if (e.key === 'Backspace' && e.currentTarget.textContent === '') {
    e.preventDefault()

    const newSegments = [...props.segments]
    newSegments.splice(index, 1)

    const normalized = normalizeSegments(newSegments)

    const prevIndex = Math.max(0, index - 1)
    const prevSegment = normalized[prevIndex]

    if (normalized.length === 0) {
      props.setSegments([{ id: `seg-${props.nextId.current++}`, type: 'text', content: '' }])
      props.setActiveIndex(0)
      props.nextCursorPos.current = 0
    } else {
      props.setSegments(normalized)
      props.setActiveIndex(prevIndex)
      props.nextCursorPos.current = prevSegment ? prevSegment.content.length : 0
    }
  }

  if (e.key === 'Enter' && !e.shiftKey) {
    if (isMentionPopupOpen) return
    e.preventDefault()

    const el = e.currentTarget
    const caretPos = getCaretPosition(el)
    const currentSegment = segments[index]
    const contentBefore = currentSegment.content.slice(0, caretPos)
    const contentAfter = currentSegment.content.slice(caretPos)

    const newSegments: Segment[] = [...segments]

    // Update current segment
    newSegments[index] = { ...currentSegment, content: contentBefore }

    // Create new segment
    const newSegmentId = `seg-${nextId.current++}`
    const newTextSegment: Segment = {
      id: `seg-${nextId.current++}`,
      type: isAnnotating ? 'annotate' : 'text',
      content: contentAfter,
    }

    // Insert new segment
    newSegments.splice(index + 1, 0, newTextSegment)
    const normalized = normalizeSegments(newSegments)

    setSegments(normalized)

    const newIndex = normalized.findIndex(s => s.id === newSegmentId)
    setActiveIndex(newIndex) // Move to the new segment
    nextCursorPos.current = 0 // At the beginning of the new segment
    return
  }

  // Other keydown logic will be added here later (Backspace, etc.)
}

export const handleInput = (
  e: React.FormEvent<HTMLSpanElement>,
  index: number,
  props: UseEditorHandlersProps,
  mentionLogic: UseMentionLogicReturn,
  isAnnotating?: boolean,
) => {
  const {
    segments,
    setSegments,
    nextCursorPos,
    isProcessing,
  } = props

  if (isProcessing.current) {
    return
  }

  const currentSegment = segments[index]
  if (!currentSegment) {
    return
  }

  const newContent = e.currentTarget.textContent || ''
  const caretPos = getCaretPosition(e.currentTarget)
  const newSegments = [...segments]

  const shouldBeAnnotate = isAnnotating && newContent.trim().length > 0

  if (currentSegment.type === 'text' && shouldBeAnnotate) {
    newSegments[index] = { ...currentSegment, type: 'annotate', content: newContent }
    setSegments(newSegments)
    nextCursorPos.current = caretPos
    return
  }
  
  if (currentSegment.type === 'annotate' && !isAnnotating) {
    newSegments[index] = { ...currentSegment, type: 'text', content: newContent }
    setSegments(newSegments)
    nextCursorPos.current = caretPos
    return
  }

  if (
    currentSegment.type === 'mention' ||
    currentSegment.type === 'mention_in_progress'
  ) {
    // UNIFIED LOGIC: Both finalized and in-progress mentions are validated here.
    const query = newContent.substring(1)
    const isValid =
      newContent.startsWith('@') &&
      props.users.some(user =>
        user.name.toLowerCase().startsWith(query.toLowerCase()),
      )

    if (isValid) {
      // The content is valid for a mention. Force it into 'in_progress' state
      // to ensure the popup is open and it's ready for continued editing.
      const newSegment: Segment = {
        ...currentSegment,
        content: newContent,
        type: 'mention_in_progress',
      }
      newSegments[index] = newSegment
      setSegments(newSegments)

      mentionLogic.updateMention(newContent)
    } else {
      // The content is NO LONGER valid for a mention. Convert to text.
      const textSegment: Segment = {
        ...currentSegment,
        content: newContent,
        type: 'text',
      }
      newSegments[index] = textSegment

      // Let the robust `normalizeSegments` handle merging with neighbors.
      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)

      // After normalization, we need to find the new index and calculate the new cursor pos.
      const finalIndex = normalized.findIndex(s => s.id === textSegment.id)
      
      if (finalIndex > -1) {
        // Our segment still exists (it might have merged with a segment to the right).
        // The caret position within the segment remains correct.
        props.setActiveIndex(finalIndex)
        nextCursorPos.current = getCaretPosition(e.currentTarget)
      } else if (index > 0) {
        // Our segment was merged into the previous one.
        const prevSegment = segments[index - 1]
        const finalPrevIndex = normalized.findIndex(s => s.id === prevSegment.id)

        props.setActiveIndex(finalPrevIndex > -1 ? finalPrevIndex : 0)
        // New cursor position is the length of the previous segment + caret position in our segment.
        nextCursorPos.current = prevSegment.content.length + getCaretPosition(e.currentTarget)
      } else {
        // Fallback for a segment at index 0 that gets normalized away (e.g., it's empty)
        props.setActiveIndex(0)
        nextCursorPos.current = getCaretPosition(e.currentTarget)
      }

      // Clean up mention state
      props.setIsMentionPopupOpen(false)
      props.setMentionQuery('')
    }
  } else {
    // --- Fallback: Standard text segment update ---
    const newSegments = [...segments]
    newSegments[index] = { ...currentSegment, content: newContent }
    setSegments(newSegments)
  }

  nextCursorPos.current = caretPos
}
